/**
 * Carga de líneas payment_appliedto para los modales de pago.
 *
 * El listado dinámico devuelve 10 filas salvo `all=true`, y el PUT del pago borra
 * las filas de grupo que no lleguen en el body: una lista recortada elimina
 * aplicaciones en BD.
 *
 * La carga completa está limitada a duntower; el resto de tenants mantiene la
 * llamada previa (una página).
 */

import { paymentRefId } from "@/utils/paymentMarkPaidUpdate";
import { isDuntowerTenant } from "@/utils/dynamicModelValidationSwal";

const APPLIED_TO_URL = "/api/gotrading/dynamic/payment_appliedto";
const APPLIED_TO_PAGE_SIZE = 500;
const APPLIED_TO_MAX_PAGES = 100;

export function extractPaymentAppliedToRows(responseData) {
  if (!responseData) return [];
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData.items)) return responseData.items;
  if (Array.isArray(responseData.data)) return responseData.data;
  return [];
}

function paginationTotal(responseData) {
  const total = Number(responseData?.pagination?.total);
  return Number.isFinite(total) ? total : null;
}

/** Filas ya presentes en el pago: getOne devuelve el grupo completo. */
export function embeddedPaymentAppliedToRows(payment) {
  const rows = Array.isArray(payment?.appliedto) ? payment.appliedto : [];
  return rows.filter(
    (row) => row && typeof row === "object" && !Array.isArray(row)
  );
}

async function fetchLegacyPaymentAppliedToRows(api, paymentId) {
  const response = await api.get(APPLIED_TO_URL, {
    params: { parent: paymentId },
  });
  return { rows: extractPaymentAppliedToRows(response?.data), complete: true };
}

async function fetchAllPaymentAppliedToRows(api, paymentId) {
  const response = await api.get(APPLIED_TO_URL, {
    params: { parent: paymentId, all: "true" },
  });
  const rows = extractPaymentAppliedToRows(response?.data);
  const total = paginationTotal(response?.data);
  if (total == null || rows.length >= total) {
    return { rows, complete: true };
  }

  const collected = [];
  const seenIds = new Set();
  for (let page = 1; page <= APPLIED_TO_MAX_PAGES; page += 1) {
    const pageResponse = await api.get(APPLIED_TO_URL, {
      params: { parent: paymentId, page, perPage: APPLIED_TO_PAGE_SIZE },
    });
    const pageRows = extractPaymentAppliedToRows(pageResponse?.data);
    if (pageRows.length === 0) break;
    for (const row of pageRows) {
      const id = paymentRefId(row?._id);
      if (id) {
        if (seenIds.has(id)) continue;
        seenIds.add(id);
      }
      collected.push(row);
    }
    if (collected.length >= total) break;
  }
  return { rows: collected, complete: collected.length >= total };
}

/**
 * @returns {Promise<{rows: Array, complete: boolean}>} `complete: false` significa que
 * la lista puede estar recortada: no enviar `appliedto` en el PUT del pago.
 */
export async function loadPaymentAppliedToRows(api, payment) {
  const paymentId = paymentRefId(payment?._id);
  const embedded = embeddedPaymentAppliedToRows(payment);
  const loadAll = isDuntowerTenant();
  if (!api || !paymentId) {
    return { rows: embedded, complete: !loadAll || embedded.length > 0 };
  }

  try {
    if (!loadAll) {
      const { rows } = await fetchLegacyPaymentAppliedToRows(api, paymentId);
      return { rows: rows.length > 0 ? rows : embedded, complete: true };
    }
    const { rows, complete } = await fetchAllPaymentAppliedToRows(
      api,
      paymentId
    );
    if (rows.length >= embedded.length) {
      return { rows, complete };
    }
    return { rows: embedded, complete: true };
  } catch (error) {
    console.error(
      "Error loading payment appliedto rows:",
      error?.message || error
    );
    return { rows: embedded, complete: !loadAll || embedded.length > 0 };
  }
}
